import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import axios from "axios";
import { apiCall, API, getAuthHeaders } from "../api";
import { can, currentFYKey, formatTime12} from "../config";
import { Btn } from "./common/FormElements";
import { CalculationTrail } from "./common/CalculationTrail";

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function downloadXlsx(endpoint, filename) {
  const resp = await axios.get(`${API}${endpoint}`, {
    headers: getAuthHeaders(),
    responseType: "blob",
  });
  // Try to parse filename from Content-Disposition
  const cd = resp.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const name = match ? match[1] : filename;
  downloadBlob(resp.data, name);
}

// Where a row of each kind is put right. "machines" rows are machines that need editing,
// deleting or scheduling; "pm" rows are stray plan rows that need deleting from the plan.
// lostLogs has no target — its machine is already gone.
const PM_HEALTH_FIX = {
  unscheduled: "machines", dupMachines: "machines",
  transferred: "pm", removed: "pm", unknown: "pm", duplicates: "pm",
};

// The PM Health rows, worst first. Each maps to a list on the /reports/pm-health payload.
const PM_HEALTH_GROUPS = [
  { key: "unscheduled", label: "Unscheduled machines", tone: "text-red-600",
    hint: "On the floor with no PM due this year — getting no preventive maintenance" },
  { key: "transferred", label: "Transferred out", tone: "text-orange-600",
    hint: "Moved to another unit, but this unit is still scheduled to service them" },
  { key: "removed", label: "Removed machines", tone: "text-gray-600",
    hint: "Scrapped or removed — the PM row was left behind and can be deleted" },
  { key: "unknown", label: "Orphan rows", tone: "text-amber-600",
    hint: "No machine behind them and no revision entry explaining why" },
  { key: "duplicates", label: "Duplicate PM rows", tone: "text-purple-600",
    hint: "The same machine planned twice in one financial year" },
  { key: "dupMachines", label: "Duplicate machine codes", tone: "text-purple-600",
    hint: "Two machines share one code — no report can tell them apart" },
  { key: "lostLogs", label: "History without a machine", tone: "text-gray-600",
    hint: "Log entries whose machine was deleted — their downtime still counts" },
];

// Sizes being collected now and made mandatory later. Shown as progress rather than a
// fault, because today most of them are blank.
const MEASUREMENTS = [
  { key: "slidesize", label: "Slide size", withKey: "withSlideSize", withoutKey: "withoutSlideSize",
    parts: "Length and Width", allNeeded: "Both" },
  { key: "bedsize", label: "Bed size", withKey: "withBedSize", withoutKey: "withoutBedSize",
    parts: "Length, Width and Height", allNeeded: "All three" },
];

// The two ends of the register by age. Oldest first is the one that matters — it is the
// replacement conversation — so it leads.
const REGISTER_TOPS = [
  { key: "oldest", label: "10 oldest machines", tone: "text-orange-600",
    hint: "Longest in service. Age is counted from the year of installation." },
  { key: "newest", label: "10 newest machines", tone: "text-green-700",
    hint: "Most recently installed." },
];

// Machine Movements: the ways a machine leaves a unit, each recorded differently.
const MOVEMENT_GROUPS = [
  { key: "sold", label: "Sold", tone: "text-emerald-700",
    hint: "Sold on \u2014 off the asset register, no PM expected.",
    cols: ["Code", "Machine", "Department", "Capacity", "Make"] },
  { key: "transferredOut", label: "Transferred out", tone: "text-orange-600",
    hint: "Moved to another unit. They are on that unit's list now.",
    cols: ["Code", "Machine", "Detail", "Date", "By"] },
  { key: "transferredIn", label: "Transferred in", tone: "text-blue-600",
    hint: "Received from another unit.",
    cols: ["Code", "Machine", "Detail", "Date", "By"] },
  { key: "scrapped", label: "Scrapped", tone: "text-gray-600",
    hint: "Written off \u2014 kept for the record.",
    cols: ["Code", "Machine", "Department", "Capacity", "Make"] },
];

// The left-hand index. One section on screen at a time — the page had grown to eight
// stacked panels and finding the one you wanted meant scrolling past the rest.
const REPORT_SECTIONS = [
  { group: "Overview", items: [
    { key: "summary", label: "PM Compliance" },
    { key: "health", label: "PM Health", badge: "pmHealth" },
  ]},
  { group: "Reliability", items: [
    { key: "mtbf", label: "MTBF / MTTR" },
    { key: "top", label: "Top Breakdowns" },
    { key: "breakdowns", label: "Breakdown Details" },
    { key: "mix", label: "Maintenance Mix" },
  ]},
  { group: "Records", items: [
    { key: "entrylog", label: "Entry Record" },
  ]},
  { group: "Assets", items: [
    { key: "register", label: "Machine Register" },
    { key: "movements", label: "Machine Movements", badge: "movements" },
  ]},
  { group: "Downloads", items: [
    { key: "downloads", label: "Excel Exports" },
  ]},
];

function StatCard({ label, value, color = "text-gray-700", testId }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid={testId}>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function fmtHours(h) {
  if (h === null || h === undefined || isNaN(Number(h))) return "—";
  return `${Number(h).toFixed(2)} h`;
}

// "Open Time" / "Closed Time" — the date and clock time the breakdown started / was closed.
function fmtStamp(date, time) {
  if (!date) return "—";
  return time ? `${date} ${time}` : date;
}

// The day-by-day working behind one breakdown's Total Breakdown Time. Every figure
// comes from the server's own calculation of that row — nothing is worked out again
// here, so the explanation cannot disagree with the hours charged.
// "2026-10-13 11:00" -> "2026-10-13 11:00 AM". The trail was the last place still
// printing 24-hour, so the same repair read 13:00 here and 1:00 PM on the history card.
function stampTo12(stamp) {
  const m = /^(\S+)\s+(\d{1,2}:\d{2})$/.exec(String(stamp || "").trim());
  return m ? `${m[1]} ${formatTime12(m[2])}` : (stamp || "");
}

function BreakdownWorking({ calc }) {
  if (!calc) return <div className="text-xs text-gray-400">No working available for this entry.</div>;
  const line = (label, value, sign = "") => (
    <div className="flex justify-between gap-4">
      <span className="text-gray-600">{sign} {label}</span>
      <span className="font-mono font-semibold text-gray-800">{value} h</span>
    </div>
  );
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-600">
        <strong>{stampTo12(calc.start)}</strong> to <strong>{stampTo12(calc.end)}</strong>
        {calc.endIsAccrued && <span className="ml-1 text-amber-700">(still open — counted up to now)</span>}
      </div>
      {/* Built from shift time, not clock time. Hours the plant was shut were never
          going to count, so opening with a 24-hour-a-day figure and subtracting them
          back out only made the reader do arithmetic to arrive where this starts. */}
      <div className="max-w-sm space-y-0.5 text-[11px]">
        {line(`Shift time while down (${calc.offWindow.replace(/^outside /, "")}${(calc.workingDays || []).length ? `, ${(calc.workingDays || []).join(", ")}` : ""})`, calc.shiftHours)}
        {line(`Lunch each day (${calc.lunch})`, calc.lunchHours, "−")}
        <div className="flex justify-between gap-4 border-t pt-0.5">
          <span className="font-semibold text-gray-800">Total breakdown time</span>
          <span className="font-mono font-bold text-blue-700" data-testid="breakdown-calc-net">{calc.netHours} h</span>
        </div>
      </div>
      {calc.netHours === 0 && (
        <p className="text-[11px] text-amber-800">
          Zero because the whole repair fell outside working hours — this measures production
          time lost, not how long the fitter was there.
        </p>
      )}
      {!!(calc.days || []).length && (
        <div className="overflow-x-auto">
          <table className="text-[11px] border">
            <thead className="bg-white text-gray-500">
              <tr>{["Date", "Day", "Machine running", "Shift h", "Lunch", "Counted"].map(h =>
                <th key={h} className="border px-1.5 py-0.5 text-left whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {calc.days.map(d => (
                <tr key={d.date} className={d.counted ? "bg-white" : "bg-gray-100 text-gray-400"}>
                  <td className="border px-1.5 py-0.5 whitespace-nowrap">{d.date}</td>
                  <td className="border px-1.5 py-0.5">{d.weekday}</td>
                  {/* The hours of this day that counted — the overlap of the breakdown
                      with the running window. Everything else about the day is noise. */}
                  <td className="border px-1.5 py-0.5 whitespace-nowrap font-mono">
                    {d.countedFrom ? `${formatTime12(d.countedFrom)}\u2013${formatTime12(d.countedTo)}` : "\u2014"}
                  </td>
                  <td className="border px-1.5 py-0.5 text-right">{d.shiftHours}</td>
                  <td className="border px-1.5 py-0.5 text-right">{d.lunchHours}</td>
                  <td className="border px-1.5 py-0.5 text-right font-semibold">
                    {d.counted ? `${d.netHours} h` : d.reason}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {calc.daysTruncated > 0 && (
            <p className="mt-1 text-[11px] text-gray-500">
              …and {calc.daysTruncated} more day{calc.daysTruncated === 1 ? "" : "s"} not listed. The totals above cover all of them.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const isOpen = String(status).toUpperCase() === "OPEN";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${isOpen ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
      {isOpen ? "Open" : "Closed"}
    </span>
  );
}

// Ranked "Top Breakdown" list — bar width is relative to the worst machine in the list.
function TopBreakdownList({ title, hint, rows, valueKey, format, barClass, testId }) {
  const max = rows.reduce((m, r) => Math.max(m, Number(r[valueKey]) || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={testId}>
      <h2 className="text-base font-semibold text-gray-800">{title}</h2>
      <p className="text-xs text-gray-500 mb-3">{hint}</p>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-500">No breakdowns in the selected period.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={r.machineId || i} className="flex items-center gap-2" data-testid={`${testId}-row-${i}`}>
              <span className="text-xs text-gray-400 w-4 text-right shrink-0">{i + 1}</span>
              <div className="w-36 min-w-0 shrink-0">
                <div className="text-xs font-semibold text-gray-800 truncate" title={r.machineCode}>{r.machineCode || "—"}</div>
                <div className="text-xs text-gray-500 truncate" title={r.machineName}>{r.machineName || " "}</div>
              </div>
              <div className="flex-1 bg-gray-100 rounded-full h-2 min-w-8">
                <div className={`${barClass} h-2 rounded-full`} style={{ width: `${max > 0 ? ((Number(r[valueKey]) || 0) / max) * 100 : 0}%` }} />
              </div>
              <span className="text-xs font-bold text-gray-700 w-20 text-right shrink-0">{format(r[valueKey])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Reports({ user, units, currentUnitId, onFixPMHealth = () => {} }) {
  const isSuper = user?.role === "superadmin";
  // Anyone allowed to switch units can report across them; the server applies the
  // same rule, so offering fewer units here would just hide data they may read.
  const canCrossUnit = isSuper || can(user, "switchUnits");
  const visibleUnits = canCrossUnit ? units : units.filter(u => u.id === user?.unitId);

  const [unitId, setUnitId] = useState(currentUnitId || user?.unitId || (visibleUnits[0]?.id || ""));

  // BUGFIX 2026-05-16: when the top UnitSwitcher changes currentUnitId,
  // sync this view to it. Without this, Reports stayed pinned to its mount-time unit.
  // Note: also remounted via key={currentUnitId} in App.js as belt-and-braces, so
  // this useEffect is the safety net if the key prop is ever removed.
  useEffect(() => {
    if (currentUnitId && currentUnitId !== unitId) {
      setUnitId(currentUnitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUnitId]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Breakdown status filter — applies to the reliability / breakdown sections.
  // A breakdown stays "Open" until someone stamps its End Date.
  const [status, setStatus] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState("");
  const [reliability, setReliability] = useState(null);
  const [relLoading, setRelLoading] = useState(false);
  const [showRelWorking, setShowRelWorking] = useState(false);
  // Which breakdown rows have their day-by-day working open. Keyed by row id so the
  // table can keep more than one expanded while they are compared.
  const [openCalcRows, setOpenCalcRows] = useState({});
  const [pmHealth, setPmHealth] = useState(null);
  const [pmHealthLoading, setPmHealthLoading] = useState(false);
  const [pmOpenGroup, setPmOpenGroup] = useState("");
  const [section, setSection] = useState("summary");
  const [movements, setMovements] = useState(null);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [entryLog, setEntryLog] = useState(null);
  const [entryLogLoading, setEntryLogLoading] = useState(false);
  const [entryLogUser, setEntryLogUser] = useState("ALL");
  const [register, setRegister] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerExporting, setRegisterExporting] = useState(false);
  const [restoring, setRestoring] = useState("");
  const [regFilters, setRegFilters] = useState({ dept: "ALL", status: "ALL", make: "ALL", search: "" });
  const pmHealthFY = currentFYKey();

  const loadSummary = useCallback(async () => {
    if (!unitId) return;
    setLoading(true); setError("");
    try {
      const params = [];
      if (fromDate) params.push(`fromDate=${encodeURIComponent(fromDate)}`);
      if (toDate) params.push(`toDate=${encodeURIComponent(toDate)}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const data = await apiCall("GET", `/reports/pm-compliance/${unitId}${qs}`);
      setSummary(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load report");
      setSummary(null);
    } finally { setLoading(false); }
  }, [unitId, fromDate, toDate]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadReliability = useCallback(async () => {
    if (!unitId) return;
    setRelLoading(true);
    try {
      const now = new Date();
      const fyStart = (now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1);
      const f = fromDate || `${fyStart}-04-01`;
      const t = toDate || now.toISOString().split("T")[0];
      const data = await apiCall("GET", `/reports/reliability/${unitId}?fromDate=${f}&toDate=${t}&status=${status}`);
      setReliability(data);
    } catch (e) {
      setReliability(null);
    } finally { setRelLoading(false); }
  }, [unitId, fromDate, toDate, status]);

  useEffect(() => { loadReliability(); }, [loadReliability]);

  // PM Health is about the plan as it stands, not a date range, so it ignores
  // From/To and always reports the current financial year.
  const loadPmHealth = useCallback(async () => {
    if (!unitId) return;
    setPmHealthLoading(true);
    try {
      const data = await apiCall("GET", `/reports/pm-health/${unitId}?fy=${encodeURIComponent(pmHealthFY)}`);
      setPmHealth(data);
    } catch (e) {
      setPmHealth(null);
    } finally { setPmHealthLoading(false); }
  }, [unitId, pmHealthFY]);

  useEffect(() => { loadPmHealth(); }, [loadPmHealth]);

  // Who filed each entry. This one DOES take From/To — "which entries went in last
  // week, and by whom" is the question being asked.
  const loadEntryLog = useCallback(async () => {
    if (!unitId) return;
    setEntryLogLoading(true);
    try {
      const p = new URLSearchParams();
      if (fromDate) p.set("fromDate", fromDate);
      if (toDate) p.set("toDate", toDate);
      const q = p.toString();
      setEntryLog(await apiCall("GET", `/reports/entry-log/${unitId}${q ? `?${q}` : ""}`));
    } catch { setEntryLog(null); }
    finally { setEntryLogLoading(false); }
  }, [unitId, fromDate, toDate]);

  useEffect(() => { loadEntryLog(); }, [loadEntryLog]);

  // Where machines have gone: sold, scrapped, or transferred to another unit. Not a
  // date-range question, so it ignores From/To like PM Health does.
  const loadMovements = useCallback(async () => {
    if (!unitId) return;
    setMovementsLoading(true);
    try { setMovements(await apiCall("GET", `/reports/machine-movements/${unitId}`)); }
    catch { setMovements(null); }
    finally { setMovementsLoading(false); }
  }, [unitId]);

  useEffect(() => { loadMovements(); }, [loadMovements]);

  // The machine list as a whole: how many, how old, and the lot in a spreadsheet. Not a
  // date-range question either, so it ignores From/To.
  // The filters go to the server rather than being applied here, so the counts, the two
  // top-tens and the download all answer the same question. Filtering on screen would
  // leave the spreadsheet holding machines the page had already excluded.
  const registerQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (regFilters.dept !== "ALL") p.set("dept", regFilters.dept);
    if (regFilters.status !== "ALL") p.set("status", regFilters.status);
    if (regFilters.make !== "ALL") p.set("make", regFilters.make);
    if (regFilters.search.trim()) p.set("search", regFilters.search.trim());
    const q = p.toString();
    return q ? `?${q}` : "";
  }, [regFilters]);

  const loadRegister = useCallback(async () => {
    if (!unitId) return;
    setRegisterLoading(true);
    try { setRegister(await apiCall("GET", `/reports/machine-register/${unitId}${registerQuery}`)); }
    catch { setRegister(null); }
    finally { setRegisterLoading(false); }
  }, [unitId, registerQuery]);

  useEffect(() => { loadRegister(); }, [loadRegister]);

  // A machine marked sold or scrapped by mistake. Only the two statuses that came from a
  // status change can be undone here — a transfer is a different operation and is undone
  // by transferring it back, which is why those rows carry no Restore.
  async function restoreMachine(row) {
    if (!window.confirm(
      `Put ${row.name} (${row.machineCode}) back on the floor?\n\n` +
      "It returns to this unit as Active, and the PM months cleared when it was retired " +
      "are put back from its maintenance frequency.\n\n" +
      "Anything already recorded as done is untouched.")) return;
    setRestoring(row.machineCode);
    try {
      const res = await apiCall("POST", `/machines/${unitId}/${row.id}/restore`);
      await loadMovements();
      await loadRegister();
      alert(`${res.message}.` + (res.monthsRestored
        ? ` ${res.monthsRestored} PM month${res.monthsRestored === 1 ? "" : "s"} put back.`
        : " No PM months were due to be put back — check its maintenance frequency."));
    } catch (err) {
      alert("Could not restore: " + (err?.response?.data?.detail || err.message));
    } finally {
      setRestoring("");
    }
  }

  async function downloadRegister() {
    setRegisterExporting(true);
    try { await downloadXlsx(`/reports/machine-register/${unitId}/export${registerQuery}`, "Machine-Register.xlsx"); }
    catch (err) { alert("Could not download: " + (err?.response?.data?.detail || err.message)); }
    finally { setRegisterExporting(false); }
  }

  const setReg = (k, v) => setRegFilters(f => ({ ...f, [k]: v }));
  // Restoring is a status change, gated the same way editing a machine is.
  const canRestore = can(user, "editMachines");
  const regFiltersOn = register
    && (regFilters.dept !== "ALL" || regFilters.status !== "ALL"
        || regFilters.make !== "ALL" || !!regFilters.search.trim());

  function buildQS() {
    const params = [];
    if (fromDate) params.push(`fromDate=${encodeURIComponent(fromDate)}`);
    if (toDate) params.push(`toDate=${encodeURIComponent(toDate)}`);
    return params.length ? `?${params.join("&")}` : "";
  }

  async function handleDownload(kind) {
    setDownloading(kind);
    try {
      const qs = buildQS();
      if (kind === "pm") {
        await downloadXlsx(`/reports/pm-compliance/${unitId}/download.xlsx${qs}`, `pm_compliance_${unitId}.xlsx`);
      } else if (kind === "history") {
        await downloadXlsx(`/reports/history-summary/${unitId}/download.xlsx${qs}`, `history_summary_${unitId}.xlsx`);
      } else if (kind === "audit") {
        await downloadXlsx(`/reports/audit-trail/download.xlsx${qs}`, `audit_trail.xlsx`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Download failed");
    } finally { setDownloading(""); }
  }

  const counts = summary?.counts || {};
  const compliancePct = summary?.compliance_pct ?? 0;
  const complianceColor = compliancePct >= 90 ? "text-green-600" : compliancePct >= 70 ? "text-yellow-600" : "text-red-600";

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Reports</h1>
          <p className="text-xs text-gray-500">Pick a section on the left. Filters apply to every section.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="text-xs text-gray-500 font-medium">Unit</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white mt-0.5"
            value={unitId}
            onChange={e => setUnitId(e.target.value)}
            disabled={!canCrossUnit && visibleUnits.length <= 1}
            data-testid="reports-unit-select"
          >
            {visibleUnits.map(u => <option key={u.id} value={u.id}>{u.shortName || u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium">From Date</label>
          <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="reports-from-date" />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium">To Date</label>
          <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="reports-to-date" />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium">Status</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white mt-0.5"
            value={status}
            onChange={e => setStatus(e.target.value)}
            data-testid="reports-status-filter"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="flex items-end">
          <Btn onClick={() => { loadSummary(); loadReliability(); }} testId="reports-refresh-btn">Refresh Summary</Btn>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="reports-error">{error}</div>}

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Section index */}
        <nav className="w-full lg:w-56 shrink-0 bg-white border border-gray-200 rounded-lg p-2" data-testid="reports-nav">
          {REPORT_SECTIONS.map(grp => {
            const items = grp.items.filter(it => it.key !== "audit" || isSuper);
            if (!items.length) return null;
            return (
              <div key={grp.group} className="mb-1 last:mb-0">
                <div className="px-2 pt-2 pb-1 text-[10px] font-semibold tracking-[0.12em] text-gray-400 uppercase">{grp.group}</div>
                {items.map(it => {
                  const active = section === it.key;
                  const badge = it.badge === "pmHealth" ? (pmHealth?.counts?.total || 0)
                    : it.badge === "movements" ? (movements?.counts?.total || 0) : 0;
                  return (
                    <button
                      key={it.key}
                      onClick={() => setSection(it.key)}
                      className={`w-full flex items-center gap-2 text-left px-2.5 py-2 rounded-md text-sm transition ${active ? "bg-blue-50 text-blue-700 font-semibold border-l-[3px] border-blue-600" : "text-gray-600 hover:bg-gray-50 border-l-[3px] border-transparent"}`}
                      data-testid={`reports-nav-${it.key}`}
                    >
                      <span className="flex-1 min-w-0 truncate">{it.label}</span>
                      {badge > 0 && (
                        <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Selected section */}
        <div className="flex-1 min-w-0 space-y-4 w-full">

      {section === "summary" && (
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="reports-pm-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-800">PM Compliance Report</h2>
            <p className="text-xs text-gray-500">Preventive maintenance plan vs actual completion.</p>
          </div>
          <Btn onClick={() => handleDownload("pm")} disabled={!unitId || downloading === "pm"} testId="reports-download-pm-btn">
            {downloading === "pm" ? "Downloading..." : "Download Excel"}
          </Btn>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Loading summary...</div>
        ) : summary ? (
          <>
            <div className="mb-3">
              <div className="text-xs text-gray-500">Compliance Rate</div>
              <div className={`text-3xl font-bold ${complianceColor}`} data-testid="reports-compliance-pct">{compliancePct} %</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <StatCard label="Total" value={counts.TOTAL || 0} testId="reports-stat-total" />
              <StatCard label="Done" value={counts.DONE || 0} color="text-green-600" testId="reports-stat-done" />
              <StatCard label="Delayed" value={counts.DELAYED || 0} color="text-yellow-600" testId="reports-stat-delayed" />
              <StatCard label="Cancelled" value={counts.CANCELLED || 0} color="text-gray-500" testId="reports-stat-cancelled" />
              <StatCard label="Overdue" value={counts.OVERDUE || 0} color="text-red-600" testId="reports-stat-overdue" />
              <StatCard label="Pending" value={counts.PENDING || 0} color="text-blue-600" testId="reports-stat-pending" />
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500">No data.</div>
        )}
      </div>
      )}

      {section === "health" && (
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="reports-pm-health-section">
        <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-gray-800">PM Health &middot; FY {pmHealthFY}</h2>
            <p className="text-xs text-gray-500">Where the PM Plan and the Machine List disagree. Click a row to list the machines.</p>
          </div>
          {pmHealth && (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pmHealth.counts.total ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`} data-testid="pm-health-total">
              {pmHealth.counts.total ? `${pmHealth.counts.total} to review` : "All clear"}
            </span>
          )}
        </div>
        {pmHealthLoading ? (
          <div className="text-sm text-gray-500">Checking…</div>
        ) : !pmHealth ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : pmHealth.counts.total === 0 ? (
          <div className="text-sm text-gray-600">
            Every PM row matches a machine, and every machine has a plan. {pmHealth.machineCount} machines &middot; {pmHealth.planCount} rows.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {PM_HEALTH_GROUPS.map(g => {
              const rows = pmHealth[g.key] || [];
              if (!rows.length) return null;
              const open = pmOpenGroup === g.key;
              return (
                <div key={g.key}>
                  <button
                    onClick={() => setPmOpenGroup(open ? "" : g.key)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50"
                    data-testid={`pm-health-${g.key}`}
                  >
                    <span className={`w-9 text-center text-sm font-bold ${g.tone}`}>{rows.length}</span>
                    <span className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800">{g.label}</span>
                      <span className="block text-xs text-gray-500">{g.hint}</span>
                    </span>
                    <span className="text-gray-400 text-xs shrink-0">{open ? "▲" : "▼"}</span>
                  </button>
                  {open && (
                    <div className="px-3 pb-3 bg-gray-50/60">
                      <div className="max-h-56 overflow-y-auto rounded border border-gray-200 bg-white divide-y divide-gray-100">
                        {rows.map((r, i) => {
                          const fix = PM_HEALTH_FIX[g.key];
                          const canFix = !!fix && !!(r.id || r.machineCode);
                          const RowTag = canFix ? "button" : "div";
                          return (
                          <RowTag
                            key={`${r.machineCode}-${i}`}
                            onClick={canFix ? () => onFixPMHealth(fix, r) : undefined}
                            title={canFix ? (fix === "machines" ? "Open in the Machine List" : "Open in the PM Plan") : undefined}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-baseline gap-2 ${canFix ? "hover:bg-blue-50 cursor-pointer" : ""}`}
                            data-testid={canFix ? `pm-health-fix-${g.key}-${i}` : undefined}
                          >
                            <span className="font-mono font-semibold text-gray-800 shrink-0">{r.machineCode || "(no code)"}</span>
                            <span className="text-gray-500 truncate">
                              {g.key === "unscheduled" && `${r.name}${r.dept ? ` · ${r.dept}` : ""} · ${r.reason}`}
                              {g.key === "duplicates" && `${r.name} · two rows: ${(r.rows || []).join(" + ")}`}
                              {(g.key === "dupMachines" || g.key === "lostLogs") && `${r.name ? `${r.name} · ` : ""}${r.reason}`}
                              {(g.key === "removed" || g.key === "transferred" || g.key === "unknown") &&
                                (r.note || "no revision entry — never in the machine list")}
                            </span>
                            {!!(r.months || []).length && (
                              <span className="ml-auto shrink-0 text-[10px] text-gray-400">{r.months.join(", ")}</span>
                            )}
                            {canFix && <span className="ml-auto shrink-0 text-[10px] text-blue-500 font-medium">Fix &rsaquo;</span>}
                          </RowTag>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {section === "entrylog" && (
        <div className="space-y-3" data-testid="entry-log-report">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-base font-semibold text-gray-800">Entry Record</h2>
            <p className="text-xs text-gray-500">
              Every maintenance entry with the account that filed it, its id and the date.
              Uses the From / To dates above, on the incident date.
            </p>
            {entryLogLoading ? (
              <p className="mt-3 text-sm text-gray-500">Loading…</p>
            ) : !entryLog ? (
              <p className="mt-3 text-sm text-gray-500">No entries for this unit and period.</p>
            ) : (
              <>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <StatCard label="Entries" value={entryLog.total} testId="entry-log-total" />
                  <StatCard label="People" value={entryLog.by_user.length} />
                  <StatCard label="No author recorded" value={entryLog.no_author_recorded}
                    color={entryLog.no_author_recorded ? "text-amber-600" : "text-gray-700"} />
                </div>
                {/* Click a name to see only their entries. The counts come from the
                    server over the same window, so they always add up to Entries. */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button onClick={() => setEntryLogUser("ALL")}
                    className={`rounded-full border px-2.5 py-1 text-xs ${entryLogUser === "ALL"
                      ? "border-blue-600 bg-blue-600 text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                    All <span className="font-bold tabular-nums">{entryLog.total}</span>
                  </button>
                  {entryLog.by_user.map(u => (
                    <button key={u.name} onClick={() => setEntryLogUser(u.name)}
                      className={`rounded-full border px-2.5 py-1 text-xs ${entryLogUser === u.name
                        ? "border-blue-600 bg-blue-600 text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                      {u.name} <span className="font-bold tabular-nums">{u.count}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {entryLog && !entryLogLoading && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr className="border-b">
                    {["Incident date", "Machine", "Description", "Type", "Entered by", "Filed on", "Signed (typed)", "Entry ID"]
                      .map(h => <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {entryLog.rows
                    .filter(r => entryLogUser === "ALL" || (r.entered_by.name || "(not recorded)") === entryLogUser)
                    .map(r => (
                    <tr key={r.entry_id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">{r.start_date} {formatTime12(r.start_time, r.entered_at)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className="font-mono text-gray-700">{r.machine_code || "—"}</span>
                        <span className="block text-[10px] text-gray-400">{r.machine_name}</span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-700">{r.description || "—"}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                          r.is_breakdown ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>
                          {r.maintenance_type_label}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className={`font-medium ${r.entered_by.known ? "text-gray-800" : "text-amber-600"}`}>
                          {r.entered_by.name || "— not recorded —"}
                        </span>
                        {r.entered_by.role && <span className="block text-[10px] text-gray-400">{r.entered_by.role}</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                        {r.entered_at ? r.entered_at.slice(0, 16).replace("T", " ") : "—"}
                        {r.edited_by && (
                          <span className="block text-[10px] text-amber-600"
                            title={`Last edited ${r.edited_at}`}>edited by {r.edited_by.name}</span>
                        )}
                      </td>
                      {/* The typed name beside the account, because they can differ and
                          that is the thing worth seeing. */}
                      <td className={`px-3 py-1.5 whitespace-nowrap ${
                        r.signed_by && r.entered_by.name && r.signed_by.trim().toLowerCase() !== r.entered_by.name.trim().toLowerCase()
                          ? "text-amber-600" : "text-gray-500"}`}>
                        {r.signed_by || "—"}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-gray-400">{r.entry_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {section === "register" && (
      <div className="space-y-4" data-testid="reports-register-section">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800">Machine Register</h2>
              <p className="text-xs text-gray-500">
                Every machine in the unit, counted. The download carries the full list with all
                its details, plus these summaries on their own sheets.
              </p>
            </div>
            <button onClick={downloadRegister} disabled={registerExporting || !register}
              className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
              data-testid="register-download-btn">
              {registerExporting ? "Preparing..." : "Download Machine List (Excel)"}
            </button>
          </div>

          <div className="flex flex-wrap gap-3 items-end mb-3 pb-3 border-b" data-testid="register-filters">
            <div className="flex-1 min-w-48">
              <label className="text-xs text-gray-500 font-medium block mb-1">Search</label>
              <input className="w-full border rounded-lg px-3 py-1.5 text-sm" placeholder="Name, machine no. or make..."
                value={regFilters.search} onChange={e => setReg("search", e.target.value)}
                data-testid="register-search" />
            </div>
            {[["dept", "Department", "departments"], ["status", "Status", "statuses"], ["make", "Make", "makes"]].map(
              ([key, label, optKey]) => (
              <div key={key}>
                <label className="text-xs text-gray-500 font-medium block mb-1">{label}</label>
                <select className="border rounded-lg px-3 py-1.5 text-sm bg-white min-w-36" value={regFilters[key]}
                  onChange={e => setReg(key, e.target.value)} data-testid={`register-${key}-filter`}>
                  <option value="ALL">All</option>
                  {(register?.options?.[optKey] || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            {regFiltersOn && (
              <button onClick={() => setRegFilters({ dept: "ALL", status: "ALL", make: "ALL", search: "" })}
                className="border border-red-300 text-red-500 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50"
                data-testid="register-clear-filters">Clear</button>
            )}
          </div>

          {register?.filtered && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 mb-3"
              data-testid="register-filtered-note">
              Showing <strong>{register.counts.total}</strong> of {register.totalInUnit} machines.
              The counts, both lists and the download all follow this filter.
            </p>
          )}

          {registerLoading ? (
            <div className="text-sm text-gray-500">Loading&hellip;</div>
          ) : !register ? (
            <div className="text-sm text-gray-500">No data.</div>
          ) : register.counts.total === 0 ? (
            <div className="text-sm text-gray-600" data-testid="register-empty">
              No machines match this filter.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <StatCard label="Machines" value={register.counts.total} testId="register-total" />
                <StatCard label="Departments" value={register.counts.departments} />
                <StatCard label="Oldest year" value={register.counts.oldestYear ?? "\u2014"} color="text-orange-600" />
                <StatCard label="Newest year" value={register.counts.newestYear ?? "\u2014"} color="text-green-700" />
                <StatCard label="Average age" value={register.counts.averageAge != null ? `${register.counts.averageAge} yr` : "\u2014"} />
                <StatCard label="No year recorded" value={register.counts.withoutYear}
                  color={register.counts.withoutYear ? "text-red-600" : "text-gray-700"} testId="register-noyear" />
              </div>
              {/* Bed size is being collected now and becomes mandatory later. Shown as
                  progress rather than a fault, because today most of them are blank. */}
              <div className="mt-3 space-y-2">
                {MEASUREMENTS.map(m => (
                  <div key={m.key}
                    className="flex flex-wrap items-center gap-3 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                    data-testid={`register-${m.key}`}>
                    <span className="font-semibold text-amber-800">{m.label}</span>
                    <span className="text-gray-700">
                      <strong>{register.counts[m.withKey]}</strong> of {register.counts.total} recorded
                    </span>
                    {register.counts[m.withoutKey] > 0 && (
                      <span className="text-amber-700">
                        {register.counts[m.withoutKey]} still to be measured — add {m.parts} on each
                        machine&rsquo;s Edit form. {m.allNeeded} are needed before it counts.
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="hidden">
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-xs">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-1">By status</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(register.byStatus).map(([k, v]) => (
                      <span key={k} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{k} &middot; <strong>{v}</strong></span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-1">By department</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(register.byDept).map(([k, v]) => (
                      <span key={k} className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{k} &middot; <strong>{v}</strong></span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {register && REGISTER_TOPS.map(t => (
          <div key={t.key} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`register-${t.key}`}>
            <h3 className={`text-sm font-semibold ${t.tone}`}>{t.label}</h3>
            <p className="text-xs text-gray-500 mb-2">{t.hint}</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>{["#", "Year", "Age", "Machine", "Machine No.", "Department", "Capacity", "Make", "Status"].map(h => (
                    <th key={h} className="text-left px-3 py-1.5 text-gray-500 font-semibold">{h}</th>))}</tr>
                </thead>
                <tbody>
                  {(register[t.key] || []).map((r, i) => (
                    <tr key={r.id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                      <td className="px-3 py-1.5 font-semibold text-gray-800">{r.year}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.age} yr</td>
                      <td className="px-3 py-1.5 text-gray-700">{r.name}</td>
                      <td className="px-3 py-1.5 font-mono font-semibold text-blue-600">{r.machineCode}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.dept}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.capacity || "\u2014"}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.make || "\u2014"}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      )}

      {section === "movements" && (
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="reports-movements-section">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-gray-800">Machine Movements</h2>
          <p className="text-xs text-gray-500">
            Machines that have left the floor &mdash; sold, scrapped, or transferred to
            another unit. None of these are expected to carry a PM schedule.
          </p>
        </div>
        {movementsLoading ? (
          <div className="text-sm text-gray-500">Loading&hellip;</div>
        ) : !movements ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : movements.counts.total === 0 ? (
          <div className="text-sm text-gray-600" data-testid="movements-empty">Nothing has left this unit.</div>
        ) : (
          <div className="space-y-4">
            {MOVEMENT_GROUPS.map(g => {
              const rows = movements[g.key] || [];
              if (!rows.length) return null;
              const isAsset = g.key === "sold" || g.key === "scrapped";
              return (
                <div key={g.key} data-testid={`movement-group-${g.key}`}>
                  <h3 className={`text-sm font-semibold ${g.tone}`}>{g.label} &middot; {rows.length}</h3>
                  <p className="text-xs text-gray-500 mb-2">{g.hint}</p>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {g.cols.map(c => <th key={c} className="text-left px-3 py-1.5 text-gray-500 font-semibold">{c}</th>)}
                          {isAsset && canRestore && <th className="px-3 py-1.5" />}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={`${r.machineCode}-${i}`} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-mono font-semibold text-gray-800">{r.machineCode || "\u2014"}</td>
                            <td className="px-3 py-1.5 text-gray-700">{r.name || "\u2014"}</td>
                            {isAsset ? (
                              <>
                                <td className="px-3 py-1.5 text-gray-500">{r.dept || "\u2014"}</td>
                                <td className="px-3 py-1.5 text-gray-500">{r.capacity || "\u2014"}</td>
                                <td className="px-3 py-1.5 text-gray-500">{r.make || "\u2014"}</td>
                              </>
                            ) : (
                              <>
                                <td className="px-3 py-1.5 text-gray-500">{r.details}</td>
                                <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{r.date || "\u2014"}</td>
                                <td className="px-3 py-1.5 text-gray-500">{r.by || "\u2014"}</td>
                              </>
                            )}
                            {isAsset && canRestore && (
                              <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                <button onClick={() => restoreMachine(r)} disabled={restoring === r.machineCode}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                                  title="Put this machine back on the floor of this unit"
                                  data-testid={`restore-${r.machineCode}`}>
                                  {restoring === r.machineCode ? "Restoring..." : "Restore"}
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {section === "mtbf" && (
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="reports-reliability-section">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-gray-800">Machine Reliability (MTBF / MTTR)</h2>
          <p className="text-xs text-gray-500">From unplanned breakdowns that <strong>started</strong> in the selected period, using this unit's shift timing and the Status filter. Uses the current financial year if no dates are picked.</p>
          {/* Which working day these figures are measured against. Two units on different
              shift settings are not comparable, and that has to be visible. */}
          {reliability?.summary?.shiftLabel && (
            <p className={`mt-1 text-xs ${reliability.summary.shiftSource === "unit" ? "text-slate-600" : "text-amber-700"}`} data-testid="rel-shift-basis">
              <strong>Working day:</strong> {reliability.summary.shiftLabel}
              {reliability.summary.shiftSource !== "unit" && " Set Shift Start / Shift End in Settings → Units to measure this unit on its own hours."}
            </p>
          )}
        </div>
        {relLoading ? (
          <div className="text-sm text-gray-500">Calculating…</div>
        ) : reliability && reliability.summary ? (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3 md:grid-cols-3 xl:grid-cols-6">
              <StatCard label="MTBF (h, one machine)" value={reliability.summary.mtbfHours != null ? reliability.summary.mtbfHours : "—"} color="text-blue-600" testId="rel-mtbf" />
              {/* Not MTBF divided by anything — elapsed working hours over the failure
                  count. The plant-floor question beside the engineering one. */}
              <StatCard label="Something stops every (h)" value={reliability.summary.hoursBetweenBreakdowns != null ? reliability.summary.hoursBetweenBreakdowns : "—"} color="text-blue-500" testId="rel-hours-between" />
              <StatCard label="MTTR (h)" value={reliability.summary.mttrHours != null ? reliability.summary.mttrHours : "—"} color="text-orange-600" testId="rel-mttr" />
              <StatCard label="Breakdowns" value={reliability.summary.failures || 0} color="text-red-600" testId="rel-fail" />
              <StatCard label="Downtime (h)" value={reliability.summary.downtimeHours || 0} testId="rel-down" />
              <StatCard label="Operating (h, whole fleet)" value={reliability.summary.operatingHours || 0} testId="rel-oper" />
            </div>
            <p className="-mt-1 mb-3 text-xs text-gray-500">
              MTBF is already a per-machine figure: operating hours are machine-hours
              ({reliability.summary.machineCount} machines &times; shift &times; working days), so dividing
              them by the breakdown count gives the hours <em>one</em> machine runs between its own
              failures. Beside it, &ldquo;something stops every&rdquo; is elapsed working hours over the
              same breakdown count &mdash; how long the unit runs before anything at all stops.
            </p>
            {/* Every figure above, with its formula and the numbers put into it. */}
            <div className="mb-3">
              <CalculationTrail
                steps={reliability.calculation}
                open={showRelWorking}
                onToggle={() => setShowRelWorking(v => !v)}
                testId="rel-calc"
              />
            </div>
            {/* An entry nobody closed keeps accruing and can dwarf the real figures.
                Say so plainly, and show the number from completed repairs alongside. */}
            {(reliability.summary.openCount || 0) > 0 && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="rel-open-warning">
                <strong>{fmtHours(reliability.summary.openDowntimeHours)}</strong> of the{" "}
                {fmtHours(reliability.summary.downtimeHours)} above comes from{" "}
                <strong>{reliability.summary.openCount}</strong>{" "}
                {reliability.summary.openCount === 1 ? "breakdown that is" : "breakdowns that are"} still open —
                they keep counting until an End Date is set on the log entry, so they grow every day and swamp the totals.
                {reliability.summary.closedMttrHours != null && (
                  <> From the {reliability.summary.closedCount} completed {reliability.summary.closedCount === 1 ? "repair" : "repairs"} alone,
                    MTTR is <strong>{fmtHours(reliability.summary.closedMttrHours)}</strong> over {fmtHours(reliability.summary.closedDowntimeHours)}.</>
                )}
                {" "}Set Status to <strong>Closed</strong> to see only completed work.
              </div>
            )}
            {(() => {
              const withFails = (reliability.machines || []).filter(m => m.failures > 0).sort((a, b) => b.failures - a.failures);
              return withFails.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>{["Machine", "Type", "Breakdowns", "Downtime (h)", "MTTR (h)", "MTBF (h)"].map(h => <th key={h} className="border px-2 py-1.5 text-left">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {withFails.map(m => (
                        <tr key={m.machineId} className="border-b">
                          <td className="border px-2 py-1.5 font-medium">{m.machineCode}</td>
                          <td className="border px-2 py-1.5 text-gray-600">{m.machineName}</td>
                          <td className="border px-2 py-1.5 text-center">{m.failures}</td>
                          <td className="border px-2 py-1.5 text-center">{m.downtimeHours}</td>
                          <td className="border px-2 py-1.5 text-center">{m.mttrHours != null ? m.mttrHours : "—"}</td>
                          <td className="border px-2 py-1.5 text-center">{m.mtbfHours != null ? m.mtbfHours : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="text-xs text-gray-500">No breakdowns recorded for this unit in the selected period.</div>;
            })()}
          </>
        ) : (
          <div className="text-sm text-gray-500">No data.</div>
        )}
      </div>

      )}

      {/* Top Breakdown machines — count-wise and time-wise */}
      {section === "top" && (!reliability || relLoading) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
          {relLoading ? "Calculating…" : "No breakdown data for this unit in the selected period."}
        </div>
      )}
      {section === "top" && reliability && !relLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="reports-top-breakdown">
          <TopBreakdownList
            title="Top Breakdown (Count-wise)"
            hint="Top 5 machines by number of breakdowns."
            rows={(reliability.topByCount || []).slice(0, 5)}
            valueKey="failures"
            format={v => `${v || 0}`}
            barClass="bg-red-500"
            testId="top-breakdown-count"
          />
          <TopBreakdownList
            title="Top Breakdown (Time-wise)"
            hint="Top 5 machines by total downtime."
            rows={(reliability.topByDowntime || []).slice(0, 5)}
            valueKey="downtimeHours"
            format={fmtHours}
            barClass="bg-orange-500"
            testId="top-breakdown-time"
          />
        </div>
      )}

      {/* Maintenance mix — where Predictive / PM / R&M work shows up */}
      {section === "mix" && (!reliability || relLoading || !(reliability.typeCounts || []).length) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
          {relLoading ? "Calculating…" : "No maintenance entries for this unit in the selected period."}
        </div>
      )}
      {section === "mix" && reliability && !relLoading && (reliability.typeCounts || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="reports-type-mix">
          <h2 className="text-base font-semibold text-gray-800">Maintenance Mix</h2>
          <p className="text-xs text-gray-500 mb-3">
            All maintenance entries opened in the selected period, by type. Only Breakdown entries
            count towards MTBF / MTTR — Scheduled PM, Predictive Maintenance and Repair &amp; Maintenance
            are planned work.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {reliability.typeCounts.map(t => (
              <div key={t.type}
                className={`border rounded-lg p-3 ${t.type === "breakdown" ? "border-red-200 bg-red-50" : "border-gray-200"}`}
                data-testid={`type-count-${t.type}`}>
                <div className="text-xs text-gray-500 font-medium">{t.label}</div>
                <div className={`text-2xl font-bold mt-1 ${t.type === "breakdown" ? "text-red-600" : "text-gray-700"}`}>{t.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breakdown details — Open Time / Closed Time / Total Breakdown Time / Status */}
      {section === "breakdowns" && (!reliability || relLoading) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
          {relLoading ? "Calculating…" : "No breakdown data for this unit in the selected period."}
        </div>
      )}
      {section === "breakdowns" && reliability && !relLoading && (() => {
        const rows = reliability.breakdowns || [];
        const s = reliability.summary || {};
        const totalTime = rows.reduce((t, r) => t + (Number(r.downtimeHours) || 0), 0);
        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="reports-breakdown-details">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Breakdown Details</h2>
                <p className="text-xs text-gray-500">
                  {status === "open" ? "Open breakdowns only" : status === "closed" ? "Closed breakdowns only" : "All breakdowns"} that
                  started between the From and To dates. Total breakdown time counts only hours the unit was running &mdash;
                  {reliability?.summary?.shiftSource === "unit"
                    ? ` outside ${reliability.summary.shiftStart}-${reliability.summary.shiftEnd}, the lunch break and non-working days are all left out.`
                    : " the 05:30\u201308:30 off window, the lunch break and non-working days are all left out."}
                </p>
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap" data-testid="breakdown-counts">
                <span className="font-semibold text-gray-800">{rows.length}</span> entries &middot;{" "}
                <span className="font-semibold text-red-600">{s.openCount || 0}</span> open &middot;{" "}
                <span className="font-semibold text-green-700">{s.closedCount || 0}</span> closed
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-gray-500">No breakdowns recorded for this unit in the selected period.</div>
            ) : (
              <>
                <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                      <tr>
                        {["", "Machine", "Type", "Description", "Open Time", "Closed Time", "Total Breakdown Time", "Status"].map(h => (
                          <th key={h || "expand"} className="border-b px-2 py-1.5 text-left whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const rowKey = r.id || String(i);
                        const calcOpen = !!openCalcRows[rowKey];
                        return (
                        <Fragment key={rowKey}>
                        <tr className="border-b last:border-0 hover:bg-gray-50" data-testid={`breakdown-row-${i}`}>
                          <td className="px-1 py-1.5">
                            {/* Why this row reads what it reads — the only place a 0.00 h
                                repair done on a Sunday explains itself. */}
                            <button
                              onClick={() => setOpenCalcRows(o => ({ ...o, [rowKey]: !o[rowKey] }))}
                              title="Show how this time is calculated"
                              className="w-5 text-gray-400 hover:text-blue-600 font-bold"
                              data-testid={`breakdown-calc-toggle-${i}`}
                            >{calcOpen ? "\u25B2" : "\u25BC"}</button>
                          </td>
                          <td className="px-2 py-1.5 font-medium whitespace-nowrap">{r.machineCode || "—"}</td>
                          <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{r.machineName || "—"}</td>
                          <td className="px-2 py-1.5 text-gray-700 max-w-[220px]"><div className="truncate" title={r.description}>{r.description || "—"}</div></td>
                          <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{fmtStamp(r.openDate, r.openTime)}</td>
                          <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{fmtStamp(r.closedDate, r.closedTime)}</td>
                          <td className="px-2 py-1.5 font-semibold text-gray-800 whitespace-nowrap">{fmtHours(r.downtimeHours)}</td>
                          <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                        </tr>
                        {calcOpen && (
                          <tr data-testid={`breakdown-calc-${i}`}>
                            <td colSpan={8} className="bg-slate-50 px-3 py-2 border-b">
                              <BreakdownWorking calc={r.calculation} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 sticky bottom-0">
                      <tr>
                        <td colSpan={6} className="px-2 py-1.5 text-right font-semibold text-gray-700 border-t">Total Breakdown Time</td>
                        <td className="px-2 py-1.5 font-bold text-gray-900 border-t whitespace-nowrap" data-testid="breakdown-total-time">{fmtHours(totalTime)}</td>
                        <td className="border-t" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {(s.openCount || 0) > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {s.openCount} open {s.openCount === 1 ? "entry keeps" : "entries keep"} accruing time
                    (<span className="font-semibold">{fmtHours(s.openDowntimeHours)}</span> so far) until an End Date is set on the log entry.
                  </p>
                )}
              </>
            )}
          </div>
        );
      })()}

      {section === "downloads" && (
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid="reports-history-section">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Maintenance History Summary</h2>
          <p className="text-xs text-gray-500">All breakdown / repair / corrective entries for the selected unit and period.</p>
        </div>
        <Btn onClick={() => handleDownload("history")} disabled={!unitId || downloading === "history"} testId="reports-download-history-btn">
          {downloading === "history" ? "Downloading..." : "Download Excel"}
        </Btn>
      </div>

      )}

      {/* Audit Trail (Superadmin only) */}
      {section === "downloads" && isSuper && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-testid="reports-audit-section">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Audit Trail</h2>
            <p className="text-xs text-gray-500">All system actions (create / update / delete / login etc).</p>
          </div>
          <Btn onClick={() => handleDownload("audit")} disabled={downloading === "audit"} testId="reports-download-audit-btn">
            {downloading === "audit" ? "Downloading..." : "Download Excel"}
          </Btn>
        </div>
      )}

        </div>
      </div>
    </div>
  );
}
